import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { analyzeResume, updateResumeData, getLatestResume, submitResumeFeedback } from '../../../../services/resume.service';
import { toast } from 'react-hot-toast';
import CreditBadge from '../common/CreditBadge';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import TextField from '@mui/material/TextField';
import { Loader2 } from 'lucide-react';

dayjs.extend(customParseFormat);

// 🛠️ Ultra-resilient helper to extract data from chaotic AI responses
const extractFieldByKeywords = (obj, keywords, fallback = '') => {
  if (!obj) return fallback;
  
  if (typeof obj === 'string') {
    if (keywords.includes('name') || keywords.includes('company')) return obj.replace(/^[-•*]\s*/, '').trim();
    return fallback;
  }
  
  if (typeof obj !== 'object') return fallback;

  for (const key of Object.keys(obj)) {
    const lowerKey = key.toLowerCase();
    if (keywords.some(kw => lowerKey === kw)) return obj[key];
  }
  
  for (const key of Object.keys(obj)) {
    const lowerKey = key.toLowerCase();
    if (keywords.some(kw => lowerKey.includes(kw))) return obj[key];
  }
  
  return fallback;
};

// 🌟 New bulletproof helper to safely sanitize skills into string data for the textarea
const ensureSkillsString = (skills) => {
  if (!skills) return '';
  if (Array.isArray(skills)) {
    return skills
      .map(s => {
        if (!s) return '';
        if (typeof s === 'string') return s;
        if (typeof s === 'object') {
          const category = extractFieldByKeywords(s, ['category', 'name', 'title', 'key']);
          const items = extractFieldByKeywords(s, ['items', 'skills', 'value', 'list']);
          if (category && items) return `${category}: ${items}`;
          if (items) return items;
          if (category) return category;
          return Object.values(s).filter(v => typeof v === 'string').join(', ');
        }
        return String(s);
      })
      .filter(Boolean)
      .join(' | ');
  }
  if (typeof skills === 'object') {
    return Object.values(skills).filter(v => typeof v === 'string').join(', ');
  }
  return String(skills);
};

const extractBulletsResilient = (obj) => {
  if (!obj) return [''];
  if (typeof obj === 'string') return ['']; 
  
  if (Array.isArray(obj.bullets)) return obj.bullets;
  if (Array.isArray(obj.description)) return obj.description;
  if (typeof obj.description === 'string' && obj.description) return [obj.description];
  if (typeof obj.bullets === 'string' && obj.bullets) return [obj.bullets];
  
  for (const key of Object.keys(obj)) {
    const lowerKey = key.toLowerCase();
    if (lowerKey.includes('desc') || lowerKey.includes('bullet') || lowerKey.includes('task') || lowerKey.includes('achievement')) {
      if (Array.isArray(obj[key])) return obj[key];
      if (typeof obj[key] === 'string') return [obj[key]];
    }
  }
  return [''];
};

const MonthYearPicker = ({ value, onChange, label, disabled, isPresent, onPresentChange, error, checkboxLabel }) => {
  let dateValue = null;
  if (value && value !== 'Present') {
    const parsed = value.length === 4 ? dayjs(value, 'YYYY') : dayjs(value, 'MM/YYYY');
    if (parsed.isValid()) dateValue = parsed;
  }

  const checkboxText = checkboxLabel || "Currently Work Here"; 

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center justify-between">
        <label className="text-[13px] font-semibold text-navy font-montserrat">{label}</label>
        {onPresentChange && (
          <label className="flex items-center gap-2 cursor-pointer group">
            <input
              type="checkbox"
              checked={isPresent}
              onChange={(e) => onPresentChange(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-blue focus:ring-blue transition-all"
            />
            <span className="text-[11px] font-bold text-slate-400 group-hover:text-blue transition-colors uppercase tracking-tight">{checkboxText}</span>
          </label>
        )}
      </div>
      <div className="relative">
        <DatePicker
          views={['year', 'month']}
          openTo="month"
          value={dateValue}
          onChange={(newValue) => {
            if (newValue && newValue.isValid()) {
              onChange(newValue.format('MM/YYYY'));
            }
          }}
          disabled={disabled || isPresent}
          renderInput={(params) => {
            let displayValue = params.inputProps.value;
            if (value && value.length === 4 && displayValue) {
               displayValue = value; 
            }
            return (
            <TextField
              {...params}
              fullWidth
              size="small"
              error={!!error}
              inputProps={{
                ...params.inputProps,
                value: isPresent ? 'Present' : displayValue,
                placeholder: isPresent ? 'Present' : 'MM/YYYY',
                readOnly: true,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: isPresent ? '#f8fafc' : 'white',
                  fontFamily: 'Inter, sans-serif',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                    borderWidth: '1px',
                  },
                  '&:hover fieldset': { borderColor: '#3b82f6' },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputBase-input': {
                  fontSize: '14px',
                  padding: '12px 16px',
                  color: isPresent ? '#94a3b8' : '#1e293b',
                  fontWeight: isPresent ? '700' : '400',
                  fontStyle: isPresent ? 'italic' : 'normal',
                },
                '& .MuiInputAdornment-root': {
                  marginRight: '8px'
                }
              }}
            />
          )}}
        />
      </div>
    </div>
  );
};

const ReviewDataStep = ({ onNavigate, onDataUpdate, uploadedFile: propUploadedFile, reviewedData: propReviewedData }) => {
  const queryClient = useQueryClient();
  const hasParsed = useRef(false); 

  const { register, control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      targetRole: '',
      location: '',
      linkedin: '',
      phone: '',
      email: '',
      github: '',
      website: '',
      experience: [],
      projects: [],
      leadership: [],
      education: [],
      skills: '',
      interests: '',
      summary: '',
      certifications: [], 
      awards: [],
      customSections: []
    },
    mode: "onChange"
  });

  const { fields: expFields, append: appendExp, remove: removeExp } = useFieldArray({ control, name: "experience" });
  const { fields: eduFields, append: appendEdu, remove: removeEdu } = useFieldArray({ control, name: "education" });
  const { fields: projectFields, append: appendProject, remove: removeProject } = useFieldArray({ control, name: "projects" });
  const { fields: leaderFields, append: appendLeader, remove: removeLeader } = useFieldArray({ control, name: "leadership" });
  const { fields: certFields, append: appendCert, remove: removeCert } = useFieldArray({ control, name: "certifications" });
  const { fields: awardFields, append: appendAward, remove: removeAward } = useFieldArray({ control, name: "awards" });
  const { fields: customFields, append: appendCustom, remove: removeCustom } = useFieldArray({ control, name: "customSections" });

  const createEmptyExperience = useCallback(() => ({
    company: '', location: '', position: '', dates: '', bullets: ['']
  }), []);

  const createEmptyEducation = useCallback(() => ({
    university: '', location: '', degree: '', dates: '', details: ''
  }), []);

  const userId = localStorage.getItem('user_id');
  const category = localStorage.getItem('category');

  // Feedback State
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState('');
  const [feedbackMissedSections, setFeedbackMissedSections] = useState([]);
  const [feedbackNotes, setFeedbackNotes] = useState('');
  const [feedbackBlinking, setFeedbackBlinking] = useState(false);
  const feedbackRef = useRef(null);

  const resumeId = propUploadedFile?.id || propReviewedData?.id;
  const storageKey = `feedback_FEEDBACK_STEP_2_REVIEW_${resumeId}`;

  useEffect(() => {
    if (resumeId && localStorage.getItem(storageKey)) {
      setFeedbackSubmitted(true);
    }
  }, [resumeId, storageKey]);

  const handleFeedbackSubmit = async () => {
    if (!feedbackStatus) {
      toast.error('Please select an option');
      return false;
    }
    if (feedbackStatus !== '👍 Looks good' && feedbackMissedSections.length === 0) {
      toast.error('Please select at least one missed section');
      return false;
    }
    try {
      await submitResumeFeedback({
        user_id: userId,
        category: category,
        resume_id: resumeId || null,
        step: 'FEEDBACK_STEP_2_REVIEW',
        feedback_data: {
          status: feedbackStatus,
          missedSections: feedbackMissedSections,
          notes: feedbackNotes
        }
      });
      setFeedbackSubmitted(true);
      if (resumeId) {
        localStorage.setItem(storageKey, 'true');
      }
      toast.success('Thank you for your feedback!');
      return true;
    } catch (error) {
      toast.error('Failed to submit feedback');
      return false;
    }
  };

  const parseDates = (dateStr) => {
    if (!dateStr) return { start: '', end: '', isPresent: false };

    const parts = dateStr.split(/(?:–|-|to|until)/i).map(s => s.trim());

    const normalize = (val) => {
      if (!val) return '';
      if (/present|current/i.test(val)) return '';
      if (/^\d{4}$/.test(val)) return val;

      const parsed = dayjs(val, [
        'MM/YYYY', 'M/YYYY', 'MMM YYYY', 'MMMM YYYY', 'YYYY-MM', 'YYYY/MM'
      ], true);

      if (parsed.isValid()) return parsed.format('MM/YYYY');

      const match = val.match(/\b(19|20)\d{2}\b/);
      if (match) return match[0]; 

      return '';
    };

    if (parts.length <= 1) {
      const isPres = /present|current/i.test(parts[0]);
      return {
        start: isPres ? '' : normalize(parts[0]),
        end: '',
        isPresent: isPres
      };
    }

    const isPres = /present|current/i.test(parts[1]);
    return {
      start: normalize(parts[0]),
      end: isPres ? '' : normalize(parts[1]),
      isPresent: isPres
    };
  };

  const joinDates = (start, end, isPresent, isRange = false) => {
    const startVal = start || '';
    const endValue = isPresent ? 'Present' : (end || '');

    if (isRange) {
      if (!startVal && !endValue) return '';
      return `${startVal} – ${endValue}`;
    }

    return endValue ? (startVal ? `${startVal} – ${endValue}` : endValue) : startVal;
  };

  const cleanState = (data) => {
    const cleaned = { ...data };
    delete cleaned.uploadedFile;
    delete cleaned.history;
    if (!cleaned.experience || cleaned.experience.length === 0) cleaned.experience = [createEmptyExperience()];
    if (!cleaned.education || cleaned.education.length === 0) cleaned.education = [createEmptyEducation()];
    return cleaned;
  };

  const { data: fetchedFile, isLoading: isFetching, error: fetchError } = useQuery({
    queryKey: ['resume', userId, category, propUploadedFile?.id],
    queryFn: async () => {
      let currentFile = propUploadedFile;
      if (!currentFile && userId && category) {
        const result = await getLatestResume(userId, category);
        if (result.success && result.resume) {
          currentFile = result.resume;
        }
      }
      return currentFile;
    },
    enabled: !!userId && !!category,
    staleTime: 5 * 60 * 1000
  });

  const { mutate: runAIAnalysis, isPending: isAnalyzing } = useMutation({
    mutationFn: async (file) => {
      const fname = file?.file?.filename || file?.filename || file?.resume_filename;
      const stage = file?.stage || 1;
      return await analyzeResume(fname, userId, category, stage);
    },
    onSuccess: (result) => {
      const aiParsed = result.parsedData || {};
      const contactInfo = result.parsedSections?.contactInfo || {};
      
      const newData = {
        name: aiParsed.name || '',
        targetRole: '',
        location: aiParsed.location || '',
        linkedin: aiParsed.linkedin || contactInfo.linkedin || '',
        phone: aiParsed.phone || contactInfo.phone || '',
        email: aiParsed.email || contactInfo.email || '',
        summary: aiParsed.summary || '',
        github: aiParsed.github || contactInfo.github || '',
        website: aiParsed.website || contactInfo.website || '',
        experience: (aiParsed.experience && aiParsed.experience.length > 0)
          ? aiParsed.experience
          : [createEmptyExperience()],
        projects: (aiParsed.projects || []).map(p => ({
          name: extractFieldByKeywords(p, ['name', 'company', 'title', 'org', 'project']),
          role: extractFieldByKeywords(p, ['role', 'position', 'title']),
          link: extractFieldByKeywords(p, ['link', 'url']),
          dates: extractFieldByKeywords(p, ['date', 'duration', 'timeline', 'dates']),
          bullets: extractBulletsResilient(p)
        })),
        leadership: (aiParsed.leadership || []).map(l => ({
          name: extractFieldByKeywords(l, ['name', 'company', 'org', 'title', 'association', 'board']),
          role: extractFieldByKeywords(l, ['role', 'position', 'title']),
          dates: extractFieldByKeywords(l, ['date', 'duration', 'timeline', 'dates']),
          bullets: extractBulletsResilient(l)
        })),
        education: (aiParsed.education && aiParsed.education.length > 0)
          ? aiParsed.education
          : [createEmptyEducation()],
        skills: ensureSkillsString(aiParsed.skills),
        interests: aiParsed.interests || '',
        certifications: aiParsed.certifications || [],
        awards: aiParsed.awards || [],
        customSections: (aiParsed.customSections || []).map(sec => ({
          title: sec.sectionTitle || sec.title || '',
          description: sec.description || (sec.items ? sec.items.map(i =>
            [i.name || i.title || '', i.date || '', i.description || '', ...(i.bullets || [])].filter(Boolean).join('\n')
          ).join('\n\n') : ''),
          _originalData: sec
        }))
      };

      reset(newData);
      onDataUpdate({ reviewedData: newData });
      window.dispatchEvent(new CustomEvent('credits-updated'));

      if (aiParsed.parseError) {
        toast.error('AI had trouble parsing your resume. Please review and fill in manually.');
      }
    },
    onError: (err) => {
      console.error('AI analysis failed:', err);
      const emptyData = {
        name: '', targetRole: '', location: '', linkedin: '', phone: '', email: '', github: '', website: '',
        experience: [createEmptyExperience()],
        projects: [],
        leadership: [],
        education: [createEmptyEducation()],
        skills: '', interests: '', certifications: [], awards: [], customSections: []
      };
      reset(emptyData);
      onDataUpdate({ reviewedData: emptyData });
      toast.error('Could not automatically parse your resume. Please fill labels manually.');
    }
  });

  const { mutate: saveMutation, isPending: isSaving } = useMutation({
    mutationFn: async (data) => {
      const resumeId = fetchedFile?.id;
      if (resumeId) {
        return await updateResumeData(resumeId, cleanState(data));
      }
    },
    onSuccess: (res, variables) => {
      onNavigate('targeting-gap', {
        uploadedFile: fetchedFile,
        reviewedData: variables
      });
    },
    onError: (err) => {
      console.error('Save failed:', err);
      toast.error('Failed to save changes. Please check your connection.');
    }
  });

  useEffect(() => {
    const normalizeData = (parsed) => ({
      ...parsed,
      website: parsed.website || '',
      github: parsed.github || '',
      experience: (parsed.experience && parsed.experience.length > 0) ? parsed.experience : [createEmptyExperience()],
      education: (parsed.education && parsed.education.length > 0) ? parsed.education : [createEmptyEducation()],
      projects: (parsed.projects || []).map(p => ({
        name: extractFieldByKeywords(p, ['name', 'company', 'title', 'org', 'project']),
        role: extractFieldByKeywords(p, ['role', 'position', 'title']),
        link: extractFieldByKeywords(p, ['link', 'url']),
        dates: extractFieldByKeywords(p, ['date', 'duration', 'timeline', 'dates']),
        bullets: extractBulletsResilient(p)
      })),
      leadership: (parsed.leadership || []).map(l => ({
        name: extractFieldByKeywords(l, ['name', 'company', 'org', 'title', 'association', 'board']),
        role: extractFieldByKeywords(l, ['role', 'position', 'title']),
        dates: extractFieldByKeywords(l, ['date', 'duration', 'timeline', 'dates']),
        bullets: extractBulletsResilient(l)
      })),
      certifications: parsed.certifications || [], 
      awards: parsed.awards || [],
      customSections: (parsed.customSections || []).map(sec => ({
        title: sec.sectionTitle || sec.title || '',
        description: sec.description || (sec.items ? sec.items.map(i =>
          [i.name || i.title || '', i.date || '', i.description || '', ...(i.bullets || [])].filter(Boolean).join('\n')
        ).join('\n\n') : ''),
        _originalData: sec._originalData || sec
      })),
      skills: ensureSkillsString(parsed.skills)
    });

    if (propReviewedData) {
      reset(normalizeData(propReviewedData));
      return;
    }

    if (fetchedFile) {
      const existingData = fetchedFile.parsed_data;
      if (existingData) {
        const parsed = typeof existingData === 'string' ? JSON.parse(existingData) : existingData;
        const gapAnalysis = fetchedFile.gap_analysis ?
          (typeof fetchedFile.gap_analysis === 'string' ? JSON.parse(fetchedFile.gap_analysis) : fetchedFile.gap_analysis)
          : null;

        const mergedData = {
          ...normalizeData(parsed),
          gap_analysis: gapAnalysis,
          targetRole: fetchedFile.target_role || parsed.targetRole || '',
          jobDescription: fetchedFile.job_description || parsed.jobDescription || ''
        };
        reset(mergedData);
        onDataUpdate({ reviewedData: mergedData });
      } else if (!hasParsed.current) {
        hasParsed.current = true;
        runAIAnalysis(fetchedFile);
      }
    }
  }, [fetchedFile, propReviewedData, createEmptyEducation, createEmptyExperience, runAIAnalysis, onDataUpdate, reset]);

  const handleContinue = async (data) => {
    if (!feedbackSubmitted) {
      if (feedbackStatus && (feedbackStatus === '👍 Looks good' || feedbackMissedSections.length > 0)) {
        const success = await handleFeedbackSubmit();
        if (!success) return;
      } else {
        toast.error('Please complete the feedback section or click "Submit Feedback".');
        if (feedbackRef.current) {
          feedbackRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setFeedbackBlinking(true);
          setTimeout(() => setFeedbackBlinking(false), 2000);
        }
        return;
      }
    }
    if (!data.experience || data.experience.length === 0) {
      toast.error('Please add at least one professional experience entry.');
      return;
    }
    if (!data.education || data.education.length === 0) {
      toast.error('Please add at least one education degree.');
      return;
    }

    const experienceData = data.experience.map((exp, idx) => {
      const { start, end, isPresent } = parseDates(exp.dates);
      const parseFormDate = (dStr) => {
         if (!dStr) return null;
         return dStr.length === 4 ? dayjs(dStr, 'YYYY') : dayjs(dStr, 'MM/YYYY');
      };

      return {
        ...exp,
        index: idx,
        startDate: parseFormDate(start),
        endDate: isPresent ? dayjs() : parseFormDate(end),
        isPresent
      };
    });

    for (let i = 0; i < experienceData.length; i++) {
      const exp = experienceData[i];
      const displayIdx = i + 1;

      if (!exp.company?.trim()) {
        toast.error(`Experience #${displayIdx}: Company name is required.`);
        return;
      }
      if (!exp.position?.trim()) {
        toast.error(`Experience #${displayIdx}: Position is required.`);
        return;
      }
      if (!exp.startDate || !exp.startDate.isValid()) {
        toast.error(`Experience #${displayIdx}: Valid start date is required.`);
        return;
      }
      if (!exp.isPresent && (!exp.endDate || !exp.endDate.isValid())) {
        toast.error(`Experience #${displayIdx}: Valid end date is required.`);
        return;
      }

      if (!exp.isPresent && exp.startDate.isAfter(exp.endDate)) {
        toast.error(`Experience #${displayIdx}: Start date cannot be after end date.`);
        return;
      }

      if (i < experienceData.length - 1) {
        const nextExp = experienceData[i + 1];
        if (nextExp.endDate && nextExp.endDate.isValid()) {
          if (exp.startDate.isBefore(nextExp.endDate, 'month') || exp.startDate.isBefore(nextExp.endDate, 'year')) {
            toast.error(`Experience #${displayIdx} start date cannot be before Experience #${displayIdx + 1} end date.`);
            return;
          }
        }
      }
    }

    for (let i = 0; i < data.education.length; i++) {
      const edu = data.education[i];
      const displayIdx = i + 1;

      if (!edu.university?.trim()) {
        toast.error(`Education #${displayIdx}: University name is required.`);
        return;
      }
      if (!edu.degree?.trim()) {
        toast.error(`Education #${displayIdx}: Degree is required.`);
        return;
      }
    }

    // Restore full customSections structure before passing to backend
    const fullCustomSections = (data.customSections || []).map(sec => {
      // If we preserved the original structured data, use it to avoid data loss
      if (sec._originalData) {
        return sec._originalData;
      }
      // Otherwise reconstruct from flat { title, description } format
      return {
        sectionTitle: sec.sectionTitle || sec.title || '',
        items: sec.description ? sec.description.split('\n\n').filter(Boolean).map(block => {
          const lines = block.split('\n').filter(Boolean);
          return {
            name: lines[0] || '',
            description: lines.slice(1).join(' ') || '',
            bullets: lines.length > 1 ? lines.slice(1) : []
          };
        }) : []
      };
    });

    const finalData = { ...data, customSections: fullCustomSections };
    saveMutation(finalData);
  };

  const onInvalid = (errors) => {
    console.warn('Validation Errors:', errors);

    const toastOptions = {
      duration: 4000,
      position: 'top-center',
      style: {
        borderRadius: '16px',
        background: '#1e293b',
        color: '#fff',
        fontWeight: '600',
        padding: '16px 24px',
        maxWidth: '500px'
      }
    };

    let firstErrorMsg = null;

    if (errors.name) firstErrorMsg = errors.name.message || 'Full Name is required';
    else if (errors.email) firstErrorMsg = errors.email.message || 'Valid email address is required';
    else if (errors.phone) firstErrorMsg = errors.phone.message || 'Phone number is required';
    else if (errors.linkedin) firstErrorMsg = errors.linkedin.message || 'Invalid LinkedIn URL';
    else if (errors.github) firstErrorMsg = errors.github.message || 'Invalid GitHub URL';

    if (!firstErrorMsg && errors.experience) {
      const entries = Object.keys(errors.experience).sort((a, b) => Number(a) - Number(b));
      for (const idx of entries) {
        const err = errors.experience[idx];
        if (!err) continue;
        const displayIdx = Number(idx) + 1;
        if (err.company) firstErrorMsg = `Experience #${displayIdx}: Company name is missing.`;
        else if (err.position) firstErrorMsg = `Experience #${displayIdx}: Position title is missing.`;
        else if (err.dates) firstErrorMsg = `Experience #${displayIdx}: ${err.dates.message}`;
        if (firstErrorMsg) break;
      }
    }

    if (!firstErrorMsg && errors.education) {
      const entries = Object.keys(errors.education).sort((a, b) => Number(a) - Number(b));
      for (const idx of entries) {
        const err = errors.education[idx];
        if (!err) continue;
        const displayIdx = Number(idx) + 1;
        if (err.university) firstErrorMsg = `Education ${displayIdx}: University name is missing.`;
        else if (err.degree) firstErrorMsg = `Education ${displayIdx}: Degree title is missing.`;
        if (firstErrorMsg) break;
      }
    }

    if (!firstErrorMsg && errors.projects) {
      const entries = Object.keys(errors.projects).sort((a, b) => Number(a) - Number(b));
      for (const idx of entries) {
        const err = errors.projects[idx];
        if (!err) continue;
        const displayIdx = Number(idx) + 1;
        if (err.name) firstErrorMsg = `Project #${displayIdx}: Project name is missing.`;
        else if (err.role) firstErrorMsg = `Project #${displayIdx}: Role title is missing.`;
        if (firstErrorMsg) break;
      }
    }

    if (!firstErrorMsg && errors.leadership) {
      const entries = Object.keys(errors.leadership).sort((a, b) => Number(a) - Number(b));
      for (const idx of entries) {
        const err = errors.leadership[idx];
        if (!err) continue;
        const displayIdx = Number(idx) + 1;
        if (err.name) firstErrorMsg = `Leadership #${displayIdx}: Organization/Activity name is missing.`;
        else if (err.role) firstErrorMsg = `Leadership #${displayIdx}: Role title is missing.`;
        if (firstErrorMsg) break;
      }
    }

    if (firstErrorMsg) {
      toast.error(firstErrorMsg, toastOptions);
    }
  };

  const emailPattern = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
  const urlPattern = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;
  const datePattern = /^(0[1-9]|1[0-2])\/\d{4}$|^Present$/;

  const ErrorMsg = ({ error }) => {
    if (!error) return null;
    return <span className="text-[11px] text-premium-red font-semibold font-inter mt-1 animate-fade-in">{error.message}</span>;
  };

  const isLoading = isFetching || isAnalyzing || isSaving;

  if (isLoading && !watch('name')) {
    return (
      <div className="bg-slate-50 text-slate-800 font-inter flex flex-col min-h-screen">
        <div className="max-w-[1000px] mx-auto px-8 py-10 w-full flex-1">
          <div className="flex flex-col items-center justify-center min-h-[400px] animate-fade-up">
            <div className="w-[50px] h-[50px] border-4 border-blue-light border-t-blue rounded-full animate-spin mb-6"></div>
            <h3 className="text-xl font-bold text-navy font-montserrat mb-2">AI is analyzing your resume...</h3>
            <p className="text-sm text-slate-500">Extracting all information intelligently. This may take 30-60 seconds.</p>
            <Loader2 className='animate-spin mt-4' size={50} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="bg-slate-50 text-slate-800 font-inter flex flex-col min-h-screen">
        <nav className="bg-white border-b border-slate-200 sticky top-0 z-[100]">
          <div className="max-w-[1200px] mx-auto px-8 h-[68px] flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => onNavigate('dashboard')}
                type="button"
                className="flex items-center gap-2 px-4 h-10 bg-white border border-slate-200 rounded-xl text-[14px] font-bold text-slate-600 hover:text-navy hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm whitespace-nowrap"
              >
                <span className="msi text-[18px]">arrow_back</span>
                Back to Dashboard
              </button>
            </div>
            <div className="flex items-center gap-3">
              {[1, 2, 3, 4].map(num => (
                <React.Fragment key={num}>
                  <div className={`flex items-center gap-2 text-[13px] font-semibold ${num < 2 ? 'text-premium-green' : num === 2 ? 'text-blue' : 'text-slate-400'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] ${num < 2 ? 'bg-premium-green text-white' : num === 2 ? 'border-2 border-blue bg-blue-light' : 'border-2 border-slate-200'}`}>{num}</div>
                    <span className="hidden sm:inline">{['Upload', 'Review', 'Target', 'Finalize'][num - 1]}</span>
                  </div>
                  {num < 4 && <div className="w-10 h-[2px] bg-slate-200" /> }
                </React.Fragment>
              ))}
            </div>
          </div>
        </nav>

        <div className="max-w-[1000px] mx-auto px-8 py-10 w-full flex-1">
          <div className="mb-8 animate-fade-up flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div>
              <h1 className="text-[32px] font-extrabold text-navy tracking-[-0.8px] mb-2 font-montserrat">Fine-tune your profile.</h1>
              <p className="text-[15px] text-slate-500 leading-relaxed">We've parsed your resume using AI. Please verify and correct the details below.</p>
            </div>
          </div>

          <div className="flex gap-3.5 p-[18px] bg-premium-amber-light border border-amber-200 rounded-2xl mb-8 animate-fade-up" style={{ animationDelay: '0.1s' }}>
            <span className="msi text-premium-amber text-[24px]">info</span>
            <div className="flex flex-col gap-1">
              <h4 className="text-[14px] font-bold text-amber-900">Review Required</h4>
              <p className="text-[13px] text-amber-700 leading-relaxed text-balance">Double-check your experience bullet points and dates. Precise information helps our AI craft a more compelling narrative for your target role.</p>
            </div>
          </div>

          {!feedbackSubmitted ? (
            <div 
              ref={feedbackRef}
              className={`bg-white border border-slate-200 rounded-3xl p-6 shadow-sm mb-6 animate-fade-up transition-all duration-300 ${feedbackBlinking ? 'ring-4 ring-blue-500/50 scale-[1.02]' : ''}`}
            >
              <h3 className="text-[15px] font-bold text-navy mb-4">Did we get your resume right?</h3>
              <div className="flex flex-wrap gap-2 mb-4">
                {['👍 Looks good', '⚠️ A few things are off', '❌ Mostly incorrect', '❌ Didn’t parse my resume'].map(opt => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setFeedbackStatus(opt)}
                    className={`px-4 py-2 rounded-xl text-[13px] font-semibold transition-all border ${feedbackStatus === opt ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>

              {feedbackStatus && feedbackStatus !== '👍 Looks good' && (
                <div className="mb-4">
                  <p className="text-[13px] font-semibold text-slate-700 mb-2">Which sections were missed or parsed incorrectly?</p>
                  <div className="flex flex-wrap gap-2">
                    {['Name / Contact', 'Work Experience', 'Education', 'Skills', 'Projects', 'Certifications', 'Links (LinkedIn/GitHub)'].map(sec => (
                      <button
                        key={sec}
                        type="button"
                        onClick={() => {
                          setFeedbackMissedSections(prev => 
                            prev.includes(sec) ? prev.filter(s => s !== sec) : [...prev, sec]
                          )
                        }}
                        className={`px-3 py-1.5 rounded-lg text-[12px] font-medium transition-all border ${feedbackMissedSections.includes(sec) ? 'bg-amber-50 border-amber-500 text-amber-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                      >
                        {sec}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label className="text-[13px] font-semibold text-slate-700 mb-2 block">Anything specific we missed? (optional)</label>
                <input
                  type="text"
                  placeholder="Tell us more..."
                  value={feedbackNotes}
                  onChange={(e) => setFeedbackNotes(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-inter text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
              <button
                type="button"
                onClick={handleFeedbackSubmit}
                disabled={!feedbackStatus || (feedbackStatus !== '👍 Looks good' && feedbackMissedSections.length === 0)}
                className="px-5 py-2 bg-blue-500 text-white rounded-xl text-[13px] font-bold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Feedback
              </button>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-6 flex items-center gap-3">
              <span className="msi text-green-600 text-[20px]">check_circle</span>
              <p className="text-[13.5px] font-semibold text-green-800">Thank you for your feedback! It helps us improve.</p>
            </div>
          )}

          <form onSubmit={handleSubmit(handleContinue, onInvalid)}>
            {/* 1. Contact Info */}
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm mb-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-[10px] bg-blue-light text-blue flex items-center justify-center">
                  <span className="msi">contact_mail</span>
                </div>
                <h2 className="text-[19px] font-bold text-navy font-montserrat">Contact Information</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-semibold text-navy font-inter">Full Name *</label>
                  <input
                    className={`w-full px-4 py-3 bg-slate-50 border rounded-xl font-inter text-sm transition-all focus:outline-none focus:border-blue focus:bg-white focus:ring-4 focus:ring-blue-light ${errors.name ? 'border-premium-red/50 ring-4 ring-premium-red-light' : 'border-slate-200'}`}
                    type="text"
                    {...register('name', { required: 'Name is required' })}
                    maxLength={100}
                    placeholder="John Doe"
                  />
                  <ErrorMsg error={errors.name} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-semibold text-navy font-inter">Email Address *</label>
                  <input
                    className={`w-full px-4 py-3 bg-slate-50 border rounded-xl font-inter text-sm transition-all focus:outline-none focus:border-blue focus:bg-white focus:ring-4 focus:ring-blue-light ${errors.email ? 'border-premium-red/50 ring-4 ring-premium-red-light' : 'border-slate-200'}`}
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: { value: emailPattern, message: 'Invalid email format' }
                    })}
                    maxLength={100}
                    placeholder="john@example.com"
                  />
                  <ErrorMsg error={errors.email} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-semibold text-navy font-inter">Phone Number *</label>
                  <input
                    className={`w-full px-4 py-3 bg-slate-50 border rounded-xl font-inter text-sm transition-all focus:outline-none focus:border-blue focus:bg-white focus:ring-4 focus:ring-blue-light ${errors.phone ? 'border-premium-red/50 ring-4 ring-premium-red-light' : 'border-slate-200'}`}
                    type="text"
                    {...register('phone', { required: 'Phone is required' })}
                    maxLength={20}
                    placeholder="+91 9876543210"
                  />
                  <ErrorMsg error={errors.phone} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-semibold text-navy font-inter">Location</label>
                  <input className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-inter text-sm focus:outline-none focus:border-blue focus:bg-white focus:ring-4 focus:ring-blue-light" type="text" {...register('location')} maxLength={100} placeholder="City, Country" />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-semibold text-navy font-inter">LinkedIn Profile</label>
                  <input
                    className={`w-full px-4 py-3 bg-slate-50 border rounded-xl font-inter text-sm transition-all focus:outline-none focus:border-blue focus:bg-white focus:ring-4 focus:ring-blue-light ${errors.linkedin ? 'border-premium-red/50 ring-4 ring-premium-red-light' : 'border-slate-200'}`}
                    type="text"
                    {...register('linkedin', {
                      pattern: { value: urlPattern, message: 'Invalid LinkedIn URL' }
                    })}
                    maxLength={200}
                    placeholder="linkedin.com/in/username"
                  />
                  <ErrorMsg error={errors.linkedin} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-semibold text-navy font-inter">GitHub / Portfolio</label>
                  <input
                    className={`w-full px-4 py-3 bg-slate-50 border rounded-xl font-inter text-sm transition-all focus:outline-none focus:border-blue focus:bg-white focus:ring-4 focus:ring-blue-light ${errors.github ? 'border-premium-red/50 ring-4 ring-premium-red-light' : 'border-slate-200'}`}
                    type="text"
                    {...register('github', {
                      pattern: { value: urlPattern, message: 'Invalid GitHub / Portfolio URL format' }
                    })}
                    maxLength={200}
                    placeholder="github.com/username"
                  />
                  <ErrorMsg error={errors.github} />
                </div>
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-semibold text-navy font-inter">Website / Portfolio URL</label>
                  <input
                    className={`w-full px-4 py-3 bg-slate-50 border rounded-xl font-inter text-sm transition-all focus:outline-none focus:border-blue focus:bg-white focus:ring-4 focus:ring-blue-light ${errors.website ? 'border-premium-red/50 ring-4 ring-premium-red-light' : 'border-slate-200'}`}
                    type="text"
                    {...register('website', {
                      pattern: { value: urlPattern, message: 'Invalid website URL format' }
                    })}
                    maxLength={250}
                    placeholder="https://yourportfolio.com"
                  />
                  <ErrorMsg error={errors.website} />
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm mb-6">
              <div className="flex justify-between items-center mb-2">
                <label className="text-[13px] font-semibold text-navy font-inter block">Professional Summary</label>
                <span className="text-[11px] text-slate-400 font-medium font-inter">
                  {(watch('summary') || '').length}/1000
                </span>
              </div>
              <textarea
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-inter text-sm focus:outline-none focus:border-blue focus:bg-white focus:ring-4 focus:ring-blue-light min-h-[120px]"
                {...register('summary')}
                maxLength={1000}
                placeholder="Describe your professional journey..."
              />
            </div>

            {/* 2. Experience */}
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm mb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-[10px] bg-blue-light text-blue flex items-center justify-center">
                    <span className="msi">work</span>
                  </div>
                  <h2 className="text-[19px] font-bold text-navy font-montserrat">Experience *</h2>
                </div>
                <button type="button" className="inline-flex items-center gap-1.5 bg-white border border-blue text-blue rounded-[10px] px-[18px] py-2.5 text-[13px] font-bold" onClick={() => appendExp(createEmptyExperience())}>
                  <span className="msi">add_circle</span> Add Experience
                </button>
              </div>
              {expFields.map((field, index) => (
                <div key={field.id} className="relative p-6 bg-slate-50 border border-slate-200 rounded-2xl mb-8">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="w-8 h-8 rounded-full bg-blue-light text-blue flex items-center justify-center text-[12px] font-extrabold font-montserrat">
                      {index + 1}
                    </span>
                    <h3 className="text-[14px] font-bold text-navy font-montserrat uppercase tracking-tight">Experience {index + 1}</h3>
                  </div>
                  {expFields.length > 1 && (
                    <button type="button" className="absolute top-3 right-3 text-slate-400 hover:text-premium-red transition-all" onClick={() => removeExp(index)}>
                      <span className="msi text-[18px]">delete</span>
                    </button>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                    <div className="flex flex-col gap-1">
                      <label className="text-[13px] font-semibold text-navy">Company *</label>
                      <input className={`w-full px-4 py-3 bg-white border rounded-xl text-sm ${errors.experience?.[index]?.company ? 'border-premium-red' : 'border-slate-200'}`} {...register(`experience.${index}.company`, { required: 'Company is required' })} maxLength={150} />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[13px] font-semibold text-navy">Position *</label>
                      <input className={`w-full px-4 py-3 bg-white border rounded-xl text-sm ${errors.experience?.[index]?.position ? 'border-premium-red' : 'border-slate-200'}`} {...register(`experience.${index}.position`, { required: 'Position is required' })} maxLength={150} />
                    </div>
                    <div className="grid grid-cols-2 gap-4 col-span-1 sm:col-span-2">
                      <Controller
                        control={control}
                        name={`experience.${index}.dates`}
                        rules={{
                          required: 'Dates are required',
                          validate: (v) => {
                            const { start, end, isPresent } = parseDates(v);

                            if (!start) return 'Start date is required';
                            if (!isPresent && !end) return 'End date is required';

                            return true;
                          }
                        }}
                        render={({ field: dateField, fieldState: { error } }) => {
                          const d = parseDates(dateField.value);
                          return (
                            <div className="flex flex-col gap-1 col-span-1 sm:col-span-2">
                              <div className="grid grid-cols-2 gap-4">
                                <MonthYearPicker
                                  label="Start Date *"
                                  value={d.start}
                                  error={!!error}
                                  onChange={(val) => dateField.onChange(joinDates(val, d.end, d.isPresent, true))}
                                />
                                <MonthYearPicker
                                  label="End Date *"
                                  value={d.end}
                                  isPresent={d.isPresent}
                                  error={!!error}
                                  onPresentChange={(checked) => dateField.onChange(joinDates(d.start, d.end, checked, true))}
                                  onChange={(val) => dateField.onChange(joinDates(d.start, val, false, true))}
                                />
                              </div>
                              {error && <ErrorMsg error={error} />}
                            </div>
                          );
                        }}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[13px] font-semibold text-navy">Location</label>
                      <input className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm" {...register(`experience.${index}.location`)} maxLength={100} />
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <label className="text-[13px] font-semibold text-navy">Bullet Points</label>
                      <button type="button" className="text-blue text-[11px] font-bold" onClick={() => {
                        const currentBullets = watch(`experience.${index}.bullets`) || [];
                        setValue(`experience.${index}.bullets`, [...currentBullets, '']);
                      }}>+ Add Point</button>
                    </div>
                    {(watch(`experience.${index}.bullets`) || ['']).map((_, bIdx) => (
                      <div key={bIdx} className="flex flex-col gap-1">
                        <div className="flex gap-2 group/bullet">
                          <div className="flex-1 relative">
                            <input
                              className={`w-full px-4 py-2 bg-white border rounded-lg text-sm transition-all focus:ring-2 focus:ring-blue-500/5 ${errors.experience?.[index]?.bullets?.[bIdx] ? 'border-premium-red' : 'border-slate-200'}`}
                              {...register(`experience.${index}.bullets.${bIdx}`, { required: 'Point cannot be empty' })}
                              maxLength={500}
                            />
                          </div>
                          {watch(`experience.${index}.bullets`).length > 1 && (
                            <button
                              type="button"
                              className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-50 text-red-400 hover:bg-red-50 hover:text-white transition-all group-hover/bullet:opacity-100"
                              onClick={(e) => {
                                const currentBullets = watch(`experience.${index}.bullets`);
                                const newBullets = [...currentBullets];
                                newBullets.splice(bIdx, 1);
                                setValue(`experience.${index}.bullets`, newBullets, { shouldValidate: true, shouldDirty: true });
                              }}
                            >
                              <span className="msi text-[18px]">close</span>
                            </button>
                          )}
                        </div>
                        <ErrorMsg error={errors.experience?.[index]?.bullets?.[bIdx]} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* 3. Projects */}
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm mb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-[10px] bg-blue-light text-blue flex items-center justify-center">
                    <span className="msi">integration_instructions</span>
                  </div>
                  <h2 className="text-[19px] font-bold text-navy font-montserrat">Projects</h2>
                </div>
                <button type="button" className="inline-flex items-center gap-1.5 bg-white border border-blue text-blue rounded-[10px] px-[18px] py-2.5 text-[13px] font-bold" onClick={() => appendProject({ name: '', role: '', link: '', bullets: [''] })}>
                  <span className="msi">add_circle</span> Add Project
                </button>
              </div>
              {projectFields.map((field, index) => (
                <div key={field.id} className="relative p-6 bg-slate-50 border border-slate-200 rounded-2xl mb-4">
                  <button type="button" className="absolute top-3 right-3 text-slate-400 hover:text-premium-red" onClick={() => removeProject(index)}>
                    <span className="msi text-[18px]">delete</span>
                  </button>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-semibold text-navy">Project Name</label>
                      <input className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm" {...register(`projects.${index}.name`)} maxLength={150} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-semibold text-navy">Your Role</label>
                      <input className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm" {...register(`projects.${index}.role`)} maxLength={150} />
                    </div>
                    <div className="flex flex-col gap-2 col-span-1 sm:col-span-2">
                      <label className="text-[13px] font-semibold text-navy">Project Link</label>
                      <input className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm" {...register(`projects.${index}.link`)} maxLength={250} placeholder="e.g., https://github.com/..." />
                    </div>
                  </div>
                  <div className="mt-4 flex flex-col gap-1">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-[13px] font-semibold text-navy font-inter">Achievements (Bullet Points)</label>
                      <button type="button" className="text-blue text-[11px] font-bold" onClick={() => {
                        const currentBullets = watch(`projects.${index}.bullets`) || [];
                        setValue(`projects.${index}.bullets`, [...currentBullets, '']);
                      }}>+ Add Point</button>
                    </div>
                    {(watch(`projects.${index}.bullets`) || ['']).map((_, bIdx) => (
                      <div key={bIdx} className="flex flex-col gap-1 mb-2">
                        <div className="flex gap-2 group/bullet">
                          <input className={`flex-1 px-4 py-2 bg-white border rounded-lg text-sm transition-all focus:ring-2 focus:ring-blue-500/5 ${errors.projects?.[index]?.bullets?.[bIdx] ? 'border-premium-red' : 'border-slate-200'}`} {...register(`projects.${index}.bullets.${bIdx}`, { required: 'Achievement is required' })} maxLength={500} />
                          {watch(`projects.${index}.bullets`).length > 1 && (
                            <button
                              type="button"
                              className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-50 text-red-400 hover:bg-red-500 hover:text-white transition-all group-hover/bullet:opacity-100"
                              onClick={() => {
                                const currentBullets = watch(`projects.${index}.bullets`);
                                const newBullets = [...currentBullets];
                                newBullets.splice(bIdx, 1);
                                setValue(`projects.${index}.bullets`, newBullets, { shouldValidate: true, shouldDirty: true });
                              }}
                            >
                              <span className="msi text-[18px]">close</span>
                            </button>
                          )}
                        </div>
                        <ErrorMsg error={errors.projects?.[index]?.bullets?.[bIdx]} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* 4. Leadership */}
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm mb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-[10px] bg-blue-light text-blue flex items-center justify-center">
                    <span className="msi">groups</span>
                  </div>
                  <h2 className="text-[19px] font-bold text-navy font-montserrat">Leadership</h2>
                </div>
                <button type="button" className="inline-flex items-center gap-1.5 bg-white border border-blue text-blue rounded-[10px] px-[18px] py-2.5 text-[13px] font-bold" onClick={() => appendLeader({ name: '', role: '', bullets: [''] })}>
                  <span className="msi">add_circle</span> Add Leadership
                </button>
              </div>
              {leaderFields.map((field, index) => (
                <div key={field.id} className="relative p-6 bg-slate-50 border border-slate-200 rounded-2xl mb-4">
                  <button type="button" className="absolute top-3 right-3 text-slate-400 hover:text-premium-red" onClick={() => removeLeader(index)}>
                    <span className="msi text-[18px]">delete</span>
                  </button>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-semibold text-navy">Organization / Activity Name</label>
                      <input className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm" {...register(`leadership.${index}.name`)} maxLength={150} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-semibold text-navy">Your Role</label>
                      <input className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm" {...register(`leadership.${index}.role`)} maxLength={150} />
                    </div>
                    <div className="flex flex-col gap-2 col-span-1 sm:col-span-2">
                      <label className="text-[13px] font-semibold text-navy">Dates</label>
                      <input className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm" {...register(`leadership.${index}.dates`)} maxLength={50} placeholder="e.g. 01/2023 – Present" />
                    </div>
                  </div>
                  <div className="mt-4 flex flex-col gap-1">
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-[13px] font-semibold text-navy font-inter">Achievements (Bullet Points)</label>
                      <button type="button" className="text-blue text-[11px] font-bold" onClick={() => {
                        const currentBullets = watch(`leadership.${index}.bullets`) || [];
                        setValue(`leadership.${index}.bullets`, [...currentBullets, '']);
                      }}>+ Add Point</button>
                    </div>
                    {(watch(`leadership.${index}.bullets`) || ['']).map((_, bIdx) => (
                      <div key={bIdx} className="flex flex-col gap-1 mb-2">
                        <div className="flex gap-2 group/bullet">
                          <input className={`flex-1 px-4 py-2 bg-white border rounded-lg text-sm transition-all focus:ring-2 focus:ring-blue-500/5 ${errors.leadership?.[index]?.bullets?.[bIdx] ? 'border-premium-red' : 'border-slate-200'}`} {...register(`leadership.${index}.bullets.${bIdx}`, { required: 'Achievement is required' })} maxLength={500} />
                          {watch(`leadership.${index}.bullets`).length > 1 && (
                            <button
                              type="button"
                              className="w-9 h-9 flex items-center justify-center rounded-lg bg-red-50 text-red-400 hover:bg-red-500 hover:text-white transition-all group-hover/bullet:opacity-100"
                              onClick={() => {
                                const currentBullets = watch(`leadership.${index}.bullets`);
                                const newBullets = [...currentBullets];
                                newBullets.splice(bIdx, 1);
                                setValue(`leadership.${index}.bullets`, newBullets, { shouldValidate: true, shouldDirty: true });
                              }}
                            >
                              <span className="msi text-[18px]">close</span>
                            </button>
                          )}
                        </div>
                        <ErrorMsg error={errors.leadership?.[index]?.bullets?.[bIdx]} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* 5. Education */}
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm mb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-[10px] bg-blue-light text-blue flex items-center justify-center">
                    <span className="msi">school</span>
                  </div>
                  <h2 className="text-[19px] font-bold text-navy font-montserrat">Education *</h2>
                </div>
                <button type="button" className="inline-flex items-center gap-1.5 bg-white border border-blue text-blue rounded-[10px] px-[18px] py-2.5 text-[13px] font-bold" onClick={() => appendEdu(createEmptyEducation())}>
                  <span className="msi">add_circle</span> Add Degree
                </button>
              </div>
              {eduFields.map((field, index) => (
                <div key={field.id} className="relative p-6 bg-slate-50 border border-slate-200 rounded-2xl mb-5">
                  {eduFields.length > 1 && (
                    <button type="button" className="absolute top-3 right-3 text-slate-400 hover:text-premium-red" onClick={() => removeEdu(index)}>
                      <span className="msi text-[18px]">delete</span>
                    </button>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-semibold text-navy">University *</label>
                      <input
                        className={`w-full px-4 py-3 bg-white border rounded-xl text-sm ${errors.education?.[index]?.university ? 'border-premium-red' : 'border-slate-200'}`}
                        {...register(`education.${index}.university`, { required: 'University is required' })}
                        maxLength={150}
                        placeholder="e.g. Stanford University"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-semibold text-navy">Location</label>
                      <input
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm"
                        {...register(`education.${index}.location`)}
                        maxLength={100}
                        placeholder="e.g. Stanford, CA"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-semibold text-navy">Degree *</label>
                      <input
                        className={`w-full px-4 py-3 bg-white border rounded-xl text-sm ${errors.education?.[index]?.degree ? 'border-premium-red' : 'border-slate-200'}`}
                        {...register(`education.${index}.degree`, { required: 'Degree is required' })}
                        maxLength={150}
                        placeholder="e.g. B.S. in Computer Science"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 col-span-1 sm:col-span-2">
                      <Controller
                        control={control}
                        name={`education.${index}.dates`}
                        render={({ field: dateField, fieldState: { error } }) => {
                          const d = parseDates(dateField.value);
                          return (
                            <div className="flex flex-col gap-1 col-span-1 sm:col-span-2">
                              <div className="grid grid-cols-2 gap-4">
                                <MonthYearPicker
                                  label="Start Date"
                                  value={d.start}
                                  error={!!error}
                                  onChange={(val) => dateField.onChange(joinDates(val, d.end, d.isPresent, true))}
                                />
                                <MonthYearPicker
                                  label="End Date"
                                  value={d.end}
                                  isPresent={d.isPresent}
                                  error={!!error}
                                  checkboxLabel="Currently Pursuing"
                                  onPresentChange={(checked) => dateField.onChange(joinDates(d.start, d.end, checked, true))}
                                  onChange={(val) => dateField.onChange(joinDates(d.start, val, false, true))}
                                />
                              </div>
                              {error && <ErrorMsg error={error} />}
                            </div>
                          );
                        }}
                      />
                    </div>

                  </div>
                </div>
              ))}
            </div>

            {/* 6. Certifications */}
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm mb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-[10px] bg-blue-light text-blue flex items-center justify-center">
                    <span className="msi">workspace_premium</span>
                  </div>
                  <h2 className="text-[19px] font-bold text-navy font-montserrat">Certifications</h2>
                </div>
                <button type="button" className="inline-flex items-center gap-1.5 bg-white border border-blue text-blue rounded-[10px] px-[18px] py-2.5 text-[13px] font-bold" onClick={() => appendCert({ name: '', issuer: '', date: '', credentialUrl: '' })}>
                  <span className="msi">add_circle</span> Add Certification
                </button>
              </div>
              {certFields.map((field, index) => (
                <div key={field.id} className="relative p-6 bg-slate-50 border border-slate-200 rounded-2xl mb-4">
                  <button type="button" className="absolute top-3 right-3 text-slate-400 hover:text-premium-red" onClick={() => removeCert(index)}>
                    <span className="msi text-[18px]">delete</span>
                  </button>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-semibold text-navy">Certification Name</label>
                      <input className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm" {...register(`certifications.${index}.name`)} maxLength={150} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-semibold text-navy">Issuer</label>
                      <input className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm" {...register(`certifications.${index}.issuer`)} maxLength={150} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-semibold text-navy">Date</label>
                      <input className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm" {...register(`certifications.${index}.date`)} maxLength={50} placeholder="MM/YYYY" />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-semibold text-navy">Credential URL</label>
                      <input className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm" {...register(`certifications.${index}.credentialUrl`)} maxLength={250} placeholder="https://..." />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* 7. Awards */}
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm mb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-[10px] bg-blue-light text-blue flex items-center justify-center">
                    <span className="msi">emoji_events</span>
                  </div>
                  <h2 className="text-[19px] font-bold text-navy font-montserrat">Awards & Recognitions</h2>
                </div>
                <button type="button" className="inline-flex items-center gap-1.5 bg-white border border-blue text-blue rounded-[10px] px-[18px] py-2.5 text-[13px] font-bold" onClick={() => appendAward({ title: '', issuer: '', date: '', description: '' })}>
                  <span className="msi">add_circle</span> Add Award
                </button>
              </div>
              {awardFields.map((field, index) => (
                <div key={field.id} className="relative p-6 bg-slate-50 border border-slate-200 rounded-2xl mb-4">
                  <button type="button" className="absolute top-3 right-3 text-slate-400 hover:text-premium-red" onClick={() => removeAward(index)}>
                    <span className="msi text-[18px]">delete</span>
                  </button>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-semibold text-navy">Award Title</label>
                      <input className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm" {...register(`awards.${index}.title`)} maxLength={150} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-semibold text-navy">Issuer</label>
                      <input className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm" {...register(`awards.${index}.issuer`)} maxLength={150} />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-semibold text-navy">Date</label>
                      <input className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm" {...register(`awards.${index}.date`)} maxLength={50} placeholder="MM/YYYY" />
                    </div>
                    <div className="flex flex-col gap-2 col-span-1 sm:col-span-2">
                      <label className="text-[13px] font-semibold text-navy">Description</label>
                      <textarea className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm min-h-[80px]" {...register(`awards.${index}.description`)} maxLength={500} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* 8. Custom Sections */}
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm mb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-[10px] bg-blue-light text-blue flex items-center justify-center">
                    <span className="msi">dashboard_customize</span>
                  </div>
                  <h2 className="text-[19px] font-bold text-navy font-montserrat">Custom Sections</h2>
                </div>
                <button type="button" className="inline-flex items-center gap-1.5 bg-white border border-blue text-blue rounded-[10px] px-[18px] py-2.5 text-[13px] font-bold" onClick={() => appendCustom({ title: '', description: '' })}>
                  <span className="msi">add_circle</span> Add Custom Section
                </button>
              </div>
              {customFields.map((field, index) => (
                <div key={field.id} className="relative p-6 bg-slate-50 border border-slate-200 rounded-2xl mb-4">
                  <button type="button" className="absolute top-3 right-3 text-slate-400 hover:text-premium-red" onClick={() => removeCustom(index)}>
                    <span className="msi text-[18px]">delete</span>
                  </button>
                  <div className="grid grid-cols-1 gap-5">
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-semibold text-navy">Section Title</label>
                      <input className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm" {...register(`customSections.${index}.title`)} maxLength={150} placeholder="e.g. Volunteer Work, Languages, Publications" />
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-[13px] font-semibold text-navy">Description</label>
                      <textarea className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm min-h-[100px]" {...register(`customSections.${index}.description`)} maxLength={1000} placeholder="Add details, bullet points, or paragraphs here..." />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* 9. Skills */}
            <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm mb-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-[10px] bg-blue-light text-blue flex items-center justify-center">
                  <span className="msi">bolt</span>
                </div>
                <h2 className="text-[19px] font-bold text-navy font-montserrat">Skills & Interests</h2>
              </div>
              <div className="flex flex-col gap-2 mb-5">
                <div className="flex justify-between items-center mb-2">
                  <label className="text-[13px] font-semibold text-navy">Technical Skills</label>
                  <span className="text-[11px] text-slate-400 font-medium font-inter">
                    {(watch('skills') || '').length}/1000
                  </span>
                </div>
                <textarea className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-inter text-sm min-h-[100px]" {...register('skills')} maxLength={1000} />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-[13px] font-semibold text-navy">Interests</label>
                <input className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm" {...register('interests')} maxLength={500} />
              </div>
            </div>

            {/* Help */}
            <div className="p-5 bg-premium-indigo-light border border-indigo-200 rounded-2xl mt-8 animate-fade-up" style={{ animationDelay: '0.4s' }}>
              <div className="flex items-center gap-2 text-premium-indigo mb-3">
                <span className="msi text-[18px]">lightbulb</span>
                <span className="text-[14px] font-bold font-montserrat">AI Tips for Better Results</span>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <li className="flex items-start gap-2 text-[12.5px] text-indigo-700 leading-relaxed font-inter">
                  <span className="msi text-[16px] mt-0.5 shrink-0">check</span>
                  <span>Use strong action verbs (Led, Developed, Optimized).</span>
                </li>
                <li className="flex items-start gap-2 text-[12.5px] text-indigo-700 leading-relaxed font-inter">
                  <span className="msi text-[16px] mt-0.5 shrink-0">check</span>
                  <span>Quantify results where possible (%, $, hours).</span>
                </li>
                <li className="flex items-start gap-2 text-[12.5px] text-indigo-700 leading-relaxed font-inter">
                  <span className="msi text-[16px] mt-0.5 shrink-0">check</span>
                  <span>Keep bullet points between 2-5 for better focus.</span>
                </li>
                <li className="flex items-start gap-2 text-[12.5px] text-indigo-700 leading-relaxed font-inter">
                  <span className="msi text-[16px] mt-0.5 shrink-0">check</span>
                  <span>Ensure skills match your target industry's keywords.</span>
                </li>
              </ul>
            </div>

            <div className="flex items-center justify-end gap-4 mt-12 pt-8 border-t border-slate-200">
              <button
                type="button"
                className="h-[52px] px-8 bg-white border border-slate-200 text-navy font-bold rounded-xl hover:bg-slate-100 disabled:opacity-60"
                onClick={() => onNavigate('dashboard')}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="h-[52px] px-10 bg-blue-500 text-white font-bold rounded-xl hover:bg-[#0055DD] shadow-lg shadow-blue-500/20 disabled:opacity-60 flex items-center gap-2 group"
                disabled={isLoading}
              >
                {isSaving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <span>Save & Continue</span>
                    <span className="msi group-hover:translate-x-1 transition-transform">arrow_forward</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
        
        <button
          onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
          className="fixed top-1/2 -translate-y-1/2 right-8 z-[100] w-14 h-14 bg-blue-500 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-blue-600 hover:scale-105 transition-all focus:outline-none focus:ring-4 focus:ring-blue-500/30"
          title="Scroll to Bottom"
        >
          <span className="msi text-[28px]">arrow_downward</span>
        </button>
      </div>
    </LocalizationProvider>
  );
};

export default ReviewDataStep;